import React, { useState } from "react";
import {
  Box,
  Chip,
  Grid,
  Paper,
  Tab,
  Tabs,
  Typography,
} from "@material-ui/core";
import { makeStyles, useTheme } from "@material-ui/core/styles";

import { computeHealthScore, resultIssueCount } from "./auditUtils";

const useStyles = makeStyles((theme) => ({
  placeholder: {
    padding: theme.spacing(4),
    textAlign: "center",
    color: theme.palette.text.secondary,
  },
  hero: {
    padding: theme.spacing(2.5),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    background: `linear-gradient(180deg, ${theme.palette.secondary.main}08 0%, #ffffff 100%)`,
  },
  heroTop: {
    display: "flex",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    flexWrap: "wrap",
  },
  metricGrid: {
    marginTop: theme.spacing(2),
  },
  metricCard: {
    padding: theme.spacing(1.5),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    height: "100%",
  },
  tabsWrap: {
    marginTop: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    overflow: "hidden",
  },
  panel: {
    padding: theme.spacing(2.5),
  },
  timeline: {
    display: "grid",
    gap: theme.spacing(1.5),
  },
  timelineRow: {
    display: "grid",
    gridTemplateColumns: "34px minmax(0, 1fr)",
    gap: theme.spacing(1.5),
    alignItems: "stretch",
  },
  markerColumn: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  markerDot: {
    width: 16,
    height: 16,
    borderRadius: "50%",
    border: "3px solid",
    backgroundColor: "#fff",
    marginTop: theme.spacing(0.75),
  },
  markerLine: {
    width: 2,
    flex: 1,
    marginTop: theme.spacing(0.5),
    marginBottom: theme.spacing(-1),
    backgroundColor: theme.palette.divider,
  },
  nodeCard: {
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    borderTopWidth: 4,
    borderRadius: theme.shape.borderRadius,
  },
  edgeCard: {
    padding: theme.spacing(1.75, 2),
    border: `1px dashed ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.default,
  },
  cardMeta: {
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    marginTop: theme.spacing(1),
  },
  evidenceGrid: {
    display: "grid",
    gap: theme.spacing(1.5),
  },
  issueCard: {
    padding: theme.spacing(1.75),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
  },
  infoCard: {
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    height: "100%",
  },
  infoGrid: {
    display: "grid",
    gap: theme.spacing(1.25),
  },
  mono: {
    fontFamily: '"Cascadia Code", Consolas, monospace',
    fontSize: 12,
    wordBreak: "break-word",
  },
}));

function statusColor(theme, status) {
  if (status === "ok") {
    return theme.palette.success.main;
  }

  if (status === "warning") {
    return theme.palette.warning.main;
  }

  if (status === "info") {
    return theme.palette.info.main;
  }

  return theme.palette.error.main;
}

function statusLabel(status) {
  if (status === "ok") {
    return "Healthy";
  }

  if (status === "warning") {
    return "Warning";
  }

  if (status === "info") {
    return "Info";
  }

  if (status === "missing") {
    return "Missing";
  }

  return "Attention";
}

function InfoPair({ label, mono, value }) {
  return (
    <Box>
      <Typography variant="caption" color="textSecondary">
        {label}
      </Typography>
      <Typography variant="body2" className={mono ? mono : ""}>
        {value || "n/a"}
      </Typography>
    </Box>
  );
}

function TabPanel({ children, value, index }) {
  if (value !== index) {
    return null;
  }

  return <Box>{children}</Box>;
}

export default function SchainAuditDetail({ result }) {
  const classes = useStyles();
  const theme = useTheme();
  const [tab, setTab] = useState(0);

  if (!result) {
    return (
      <Paper variant="outlined" className={classes.placeholder}>
        <Typography variant="h6">Select a validated row</Typography>
        <Typography variant="body2" style={{ marginTop: 12 }}>
          The selected row will open here with a vertical payment path, issue triage, evidence, and input context.
        </Typography>
      </Paper>
    );
  }

  const chainStatus = result.supplyChain ? result.supplyChain.status : "broken";
  const healthScore = computeHealthScore(result);
  const issues = result.issues || [];
  const nodes = result.supplyChain ? result.supplyChain.nodes || [] : [];
  const edges = result.supplyChain ? result.supplyChain.edges || [] : [];
  const matchedPairs = result.matchedPairs || [];
  const terminalInfo = result.terminalInfo;

  return (
    <Box>
      <Box className={classes.hero}>
        <Box className={classes.heroTop}>
          <Box>
            <Typography variant="overline" color="textSecondary">
              Selected Row
            </Typography>
            <Typography variant="h5" style={{ marginTop: 6 }}>
              {result.targetDomain || "Unknown inventory"}
            </Typography>
            <Typography variant="body2" color="textSecondary" style={{ marginTop: 8, maxWidth: 760 }}>
              Source row {result.input ? result.input.sourceRowNumber : "n/a"} with {matchedPairs.length} resolved seller
              node{matchedPairs.length === 1 ? "" : "s"} and {resultIssueCount(result)} surfaced issue
              {resultIssueCount(result) === 1 ? "" : "s"}.
            </Typography>
          </Box>

          <Chip
            label={statusLabel(chainStatus)}
            style={{
              backgroundColor: `${statusColor(theme, chainStatus)}18`,
              color: statusColor(theme, chainStatus),
              fontWeight: 700,
            }}
          />
        </Box>

        <Grid container spacing={2} className={classes.metricGrid}>
          <Grid item xs={12} sm={6} lg={3}>
            <Box className={classes.metricCard}>
              <Typography variant="caption" color="textSecondary">Health score</Typography>
              <Typography variant="h4" style={{ color: statusColor(theme, chainStatus), marginTop: 8 }}>{healthScore}%</Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <Box className={classes.metricCard}>
              <Typography variant="caption" color="textSecondary">Resolved nodes</Typography>
              <Typography variant="h4" style={{ marginTop: 8 }}>{matchedPairs.length}</Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <Box className={classes.metricCard}>
              <Typography variant="caption" color="textSecondary">Issues</Typography>
              <Typography variant="h4" style={{ marginTop: 8 }}>{resultIssueCount(result)}</Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <Box className={classes.metricCard}>
              <Typography variant="caption" color="textSecondary">Blocking links</Typography>
              <Typography variant="h4" style={{ marginTop: 8 }}>{result.supplyChain ? result.supplyChain.edgeErrors || 0 : 0}</Typography>
            </Box>
          </Grid>
        </Grid>
      </Box>

      <Box className={classes.tabsWrap}>
        <Tabs
          value={tab}
          onChange={(_, nextTab) => setTab(nextTab)}
          indicatorColor="secondary"
          textColor="secondary"
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="Payment Path" />
          <Tab label={`Issues (${issues.length})`} />
          <Tab label={`Evidence (${matchedPairs.length})`} />
          <Tab label="Context" />
        </Tabs>

        <TabPanel value={tab} index={0}>
          <Box className={classes.panel}>
            {nodes.length === 0 ? (
              <Typography variant="body2" color="textSecondary">
                No supply chain nodes are available for this row.
              </Typography>
            ) : (
              <Box className={classes.timeline}>
                {nodes.map((node, index) => {
                  const nodeTone = statusColor(theme, node.status || "info");
                  const edge = edges[index];
                  const edgeTone = edge ? statusColor(theme, edge.status || "info") : theme.palette.divider;
                  const showLine = index < nodes.length - 1;

                  return (
                    <React.Fragment key={node.id || `${node.kind}-${index}`}>
                      <Box className={classes.timelineRow}>
                        <Box className={classes.markerColumn}>
                          <Box className={classes.markerDot} style={{ borderColor: nodeTone }} />
                          {showLine ? <Box className={classes.markerLine} style={{ backgroundColor: edgeTone }} /> : null}
                        </Box>
                        <Paper
                          variant="outlined"
                          className={classes.nodeCard}
                          style={{
                            borderTopColor: nodeTone,
                            backgroundColor: `${nodeTone}08`,
                          }}
                        >
                          <Box display="flex" justifyContent="space-between" alignItems="flex-start" style={{ gap: 12 }}>
                            <Box>
                              <Typography variant="subtitle1">{node.label || node.kind || "Node"}</Typography>
                              <Typography variant="body2" color="textSecondary" style={{ marginTop: 4 }}>
                                {node.subtitle || "No subtitle provided."}
                              </Typography>
                            </Box>
                            <Chip
                              size="small"
                              label={statusLabel(node.status)}
                              style={{
                                backgroundColor: `${nodeTone}18`,
                                color: nodeTone,
                                fontWeight: 700,
                              }}
                            />
                          </Box>

                          <Box className={classes.cardMeta}>
                            {node.sellerType ? <Chip size="small" label={node.sellerType} variant="outlined" /> : null}
                            {node.sellerName ? <Chip size="small" label={node.sellerName} variant="outlined" /> : null}
                          </Box>

                          {node.sellerDomain ? (
                            <Typography variant="body2" className={classes.mono} style={{ marginTop: 12 }}>
                              {node.sellerDomain}
                            </Typography>
                          ) : null}
                        </Paper>
                      </Box>

                      {edge ? (
                        <Box className={classes.timelineRow}>
                          <Box className={classes.markerColumn}>
                            <Box className={classes.markerDot} style={{ borderColor: edgeTone, width: 12, height: 12 }} />
                            {index < edges.length - 1 ? <Box className={classes.markerLine} /> : null}
                          </Box>
                          <Paper
                            variant="outlined"
                            className={classes.edgeCard}
                            style={{ borderColor: `${edgeTone}55` }}
                          >
                            <Box display="flex" justifyContent="space-between" alignItems="flex-start" style={{ gap: 12 }}>
                              <Box>
                                <Typography variant="subtitle2">{edge.label || "Link"}</Typography>
                                <Typography variant="body2" color="textSecondary" style={{ marginTop: 6 }}>
                                  {edge.message || "No link detail provided."}
                                </Typography>
                              </Box>
                              <Chip
                                size="small"
                                label={statusLabel(edge.status)}
                                style={{
                                  backgroundColor: `${edgeTone}18`,
                                  color: edgeTone,
                                  fontWeight: 700,
                                }}
                              />
                            </Box>
                          </Paper>
                        </Box>
                      ) : null}
                    </React.Fragment>
                  );
                })}
              </Box>
            )}
          </Box>
        </TabPanel>

        <TabPanel value={tab} index={1}>
          <Box className={classes.panel}>
            {issues.length === 0 ? (
              <Paper variant="outlined" className={classes.issueCard} style={{ backgroundColor: "#eefbf5", borderColor: "#98e3c4" }}>
                <Typography variant="subtitle2" style={{ color: "#129166" }}>No issues detected</Typography>
                <Typography variant="body2" style={{ color: "#129166", marginTop: 6 }}>
                  This row completed validation without blocking or warning-level findings.
                </Typography>
              </Paper>
            ) : (
              <Box className={classes.evidenceGrid}>
                {issues.map((issue, index) => {
                  const tone = statusColor(theme, issue.severity || "info");
                  return (
                    <Paper key={`${issue.code || "issue"}-${index}`} variant="outlined" className={classes.issueCard}>
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start" style={{ gap: 12 }}>
                        <Box>
                          <Typography variant="subtitle2">{issue.code || "ISSUE"}</Typography>
                          <Typography variant="body2" style={{ marginTop: 8 }}>
                            {issue.message || "No issue message provided."}
                          </Typography>
                        </Box>
                        <Chip
                          size="small"
                          label={issue.severity || "info"}
                          style={{
                            backgroundColor: `${tone}18`,
                            color: tone,
                            fontWeight: 700,
                          }}
                        />
                      </Box>
                    </Paper>
                  );
                })}
              </Box>
            )}
          </Box>
        </TabPanel>

        <TabPanel value={tab} index={2}>
          <Box className={classes.panel}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Paper variant="outlined" className={classes.infoCard}>
                  <Typography variant="overline" color="textSecondary">Terminal Reconciliation</Typography>
                  <Typography variant="subtitle1" style={{ marginTop: 6 }}>Equativ context</Typography>
                  <Box className={classes.infoGrid} style={{ marginTop: 16 }}>
                    <InfoPair label="Status" value={terminalInfo && terminalInfo.status} />
                    <InfoPair label="Message" value={terminalInfo && terminalInfo.message} />
                    <InfoPair
                      label="Publisher entry"
                      mono={classes.mono}
                      value={
                        terminalInfo && terminalInfo.publisherEntry
                          ? `${terminalInfo.publisherEntry.domain || "n/a"} / ${terminalInfo.publisherEntry.sellerId || "n/a"}`
                          : "Not found"
                      }
                    />
                    <InfoPair
                      label="Intermediary entry"
                      mono={classes.mono}
                      value={
                        terminalInfo && terminalInfo.intermediaryEntry
                          ? `${terminalInfo.intermediaryEntry.domain || "n/a"} / ${terminalInfo.intermediaryEntry.sellerId || "n/a"}`
                          : "Not found"
                      }
                    />
                  </Box>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper variant="outlined" className={classes.infoCard}>
                  <Typography variant="overline" color="textSecondary">Resolved Seller Records</Typography>
                  <Typography variant="subtitle1" style={{ marginTop: 6 }}>Matched evidence</Typography>
                  {matchedPairs.length === 0 ? (
                    <Typography variant="body2" color="textSecondary" style={{ marginTop: 16 }}>
                      No seller node evidence is available for this row.
                    </Typography>
                  ) : (
                    <Box className={classes.evidenceGrid} style={{ marginTop: 16 }}>
                      {matchedPairs.map((pair, index) => (
                        <Paper key={`${pair.adSystem || "pair"}-${index}`} variant="outlined" className={classes.issueCard}>
                          <Typography variant="subtitle2">
                            Node {index + 1}: {pair.adSystem || "unknown"} / {pair.sellerId || "missing"}
                          </Typography>
                          <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
                            {pair.sellerInfo
                              ? `${pair.sellerInfo.name || "Unnamed seller"} (${pair.sellerInfo.sellerType || "UNKNOWN"})`
                              : "Seller record not found in sellers.json."}
                          </Typography>
                          {pair.sellerInfo && pair.sellerInfo.domain ? (
                            <Typography variant="body2" className={classes.mono} style={{ marginTop: 10 }}>
                              {pair.sellerInfo.domain}
                            </Typography>
                          ) : null}
                          {pair.sellersJsonUrl ? (
                            <Typography variant="body2" className={classes.mono} style={{ marginTop: 8 }}>
                              {pair.sellersJsonUrl}
                            </Typography>
                          ) : null}
                        </Paper>
                      ))}
                    </Box>
                  )}
                </Paper>
              </Grid>
            </Grid>
          </Box>
        </TabPanel>

        <TabPanel value={tab} index={3}>
          <Box className={classes.panel}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Paper variant="outlined" className={classes.infoCard}>
                  <Typography variant="overline" color="textSecondary">Input Snapshot</Typography>
                  <Box className={classes.infoGrid} style={{ marginTop: 16 }}>
                    <InfoPair label="Publisher domain" mono={classes.mono} value={result.input && result.input.publisherDomain} />
                    <InfoPair label="App domain" mono={classes.mono} value={result.input && result.input.appDomain} />
                    <InfoPair label="App bundle" mono={classes.mono} value={result.input && result.input.appBundle} />
                    <InfoPair label="Expected seller IDs" mono={classes.mono} value={result.input && result.input.expectedSellerId} />
                    <InfoPair label="Expected ad systems" mono={classes.mono} value={result.input && result.input.expectedAdSystem} />
                    <InfoPair label="Inventory partner domain" mono={classes.mono} value={result.input && result.input.sellerDomain} />
                    <InfoPair label="Network ID" mono={classes.mono} value={result.input && result.input.networkId} />
                    <InfoPair label="Publisher ID" mono={classes.mono} value={result.input && result.input.publisherId} />
                  </Box>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper variant="outlined" className={classes.infoCard}>
                  <Typography variant="overline" color="textSecondary">Chain Snapshot</Typography>
                  <Box className={classes.infoGrid} style={{ marginTop: 16 }}>
                    <InfoPair label="Chain status" value={statusLabel(chainStatus)} />
                    <InfoPair label="Target inventory" mono={classes.mono} value={result.targetDomain} />
                    <InfoPair label="Resolved node count" value={String(matchedPairs.length)} />
                    <InfoPair label="Blocking links" value={String(result.supplyChain ? result.supplyChain.edgeErrors || 0 : 0)} />
                    <InfoPair label="Issue summary" value={`${issues.length} issue${issues.length === 1 ? "" : "s"}`} />
                    <InfoPair label="File type" value={result.fileType || "schain"} />
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </Box>
        </TabPanel>
      </Box>
    </Box>
  );
}
